import { MutationTree, GetterTree } from "vuex";
import { RootState } from "vuefront-store";
import { Page } from "vuefront-api";

export const STATIC_PAGE_SEO_HYDRATION_CONTRACT =
  "static-page-seo-hydration/v1" as const;

const MAX_STATIC_PAGE_SEO_ENTRIES = 16;

export type StaticPageSeoHydration = {
  contract: typeof STATIC_PAGE_SEO_HYDRATION_CONTRACT;
  path: string;
  informationId: string;
  title: string;
  description: string;
  heading: string;
  adminHeading: string;
  dateModified: string;
  keyword: string;
  robots: string;
};

function boundedText(value: unknown, maximumLength: number): string {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maximumLength);
}

function normalizedStaticPagePath(value: unknown): string {
  const raw = String(value ?? "")
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, "");
  if (!raw || !/^[a-z0-9][a-z0-9_-]{0,127}$/i.test(raw)) return "";
  return `/${raw}`;
}

/**
 * Validate and bound the small SEO payload that crosses the SSR hydration
 * boundary. This state belongs to a per-request Vuex state factory; it is not a
 * process-global cache and is capped for long-lived SPA sessions.
 */
export function normalizeStaticPageSeoHydration(
  value: unknown
): StaticPageSeoHydration | null {
  const source = value as Partial<StaticPageSeoHydration> | null;
  if (source?.contract !== STATIC_PAGE_SEO_HYDRATION_CONTRACT) return null;

  const path = normalizedStaticPagePath(source.path);
  const informationId = boundedText(source.informationId, 32);
  const title = boundedText(source.title, 512);
  const description = boundedText(source.description, 5000);
  const heading = boundedText(source.heading, 1024);
  if (!path || !/^\d{1,32}$/.test(informationId) || !title || !description) {
    return null;
  }

  return {
    contract: STATIC_PAGE_SEO_HYDRATION_CONTRACT,
    path,
    informationId,
    title,
    description,
    heading: heading || title,
    adminHeading: boundedText(source.adminHeading, 1024),
    dateModified: boundedText(source.dateModified, 64),
    keyword: boundedText(source.keyword, 512),
    robots: boundedText(source.robots, 1024),
  };
}

export type State = {
  page: Page;
  staticSeoByPath: Record<string, StaticPageSeoHydration>;
};

export const state = (): State => ({
  page: {},
  staticSeoByPath: {},
});

export const getters: GetterTree<State, RootState> = {
  get(state) {
    return state.page;
  },
  getStaticSeoByPath:
    (currentState) =>
    (path: unknown, informationId?: unknown): StaticPageSeoHydration | null => {
      const normalizedPath = normalizedStaticPagePath(path);
      if (!normalizedPath) return null;
      const payload = normalizeStaticPageSeoHydration(
        currentState.staticSeoByPath?.[normalizedPath]
      );
      if (!payload) return null;
      const expectedId = boundedText(informationId, 32);
      return expectedId && payload.informationId !== expectedId
        ? null
        : payload;
    },
};

export const mutations: MutationTree<State> = {
  setPage(state, page) {
    state.page = page;
  },
  setStaticSeo(state, value) {
    const payload = normalizeStaticPageSeoHydration(value);
    if (!payload) return;

    const next = {
      ...state.staticSeoByPath,
      [payload.path]: payload,
    };
    const paths = Object.keys(next);
    while (paths.length > MAX_STATIC_PAGE_SEO_ENTRIES) {
      const oldestPath = paths.shift();
      if (oldestPath) delete next[oldestPath];
    }
    state.staticSeoByPath = next;
  },
};
